import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { formatTimestamp } from './chatUtils';
import MessageActions from './MessageActions';
import PlanQuestionCard from './PlanQuestionCard';
import { isPlanChatMessage } from './planUtils';
import { projectJobStatus } from '../../settings/shared/constants';

/**
 * Deduplicates a message array before rendering.
 *
 * Rules (applied in order):
 * 1. If two consecutive assistant messages are both bare PROCESSING/STREAMING
 *    placeholders (no content, same jobStatus), keep only the last one.
 * 2. If a message with the same `id` appears more than once, keep the last
 *    occurrence (most up-to-date).
 *
 * Usage — call this in the parent that renders the message list:
 *   import { dedupeMessages } from './ChatMessage';
 *   const safeMessages = dedupeMessages(messages);
 *   safeMessages.map(msg => <ChatMessage key={msg.id} message={msg} ... />)
 */
export function dedupeMessages(messages = []) {
  // Step 1: dedupe by id — keep last occurrence (most up-to-date)
  const seenIds = new Map();
  messages.forEach((msg, idx) => {
    if (msg.id) seenIds.set(msg.id, idx);
  });
  const deduped = messages.filter((msg, idx) =>
    !msg.id || seenIds.get(msg.id) === idx
  );

  // Step 2: collapse consecutive bare-status assistant placeholders
  // e.g. two back-to-back PROCESSING messages with no content → keep only last
  const result = [];
  for (let i = 0; i < deduped.length; i++) {
    const curr = deduped[i];
    const prev = result[result.length - 1];

    const isBareStatus = (msg) =>
      msg &&
      msg.role === 'assistant' &&
      !msg.content &&
      !isPlanChatMessage(msg) &&
      [projectJobStatus.PROCESSING, projectJobStatus.STREAMING].includes(msg.jobStatus);

    if (isBareStatus(curr) && isBareStatus(prev)) {
      // Replace the previous placeholder with the newer one
      result[result.length - 1] = curr;
    } else {
      result.push(curr);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

const RegularChatMessage = ({ message, messageFeedback, onFeedback, onCopy }) => {
  const isUser = message.role === 'user';
  const [isExpanded, setIsExpanded] = useState(false);

  const content = message?.content || '';

  const isCompleted = [
    projectJobStatus.SUCCESS,
    projectJobStatus.COMPLETED,
    projectJobStatus.INTERRUPT,
  ].includes(message.jobStatus);

  const isProcessing = message.jobStatus === projectJobStatus.PROCESSING;
  const isStreaming  = message.jobStatus === projectJobStatus.STREAMING;

  // isStreaming always takes priority — never show both badges simultaneously
  const activeStatus = isStreaming ? 'streaming' : isProcessing ? 'processing' : null;

  const isLongUserMessage = useMemo(() => {
    const charCount = content.length;
    const lineCount = content.split('\n').length;
    return charCount > 460 || lineCount > 15;
  }, [content]);

  const canCollapse = isUser && isLongUserMessage;
  const isCollapsed = canCollapse && !isExpanded;

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-wrapper">

        {/* ── Assistant header: name + timestamp ── */}
        {!isUser && (
          <div className="message-header">
            <span className="assistant-name font-inter-bold">
              {message.title || 'SuperAgentX AI'}
            </span>
            <span className="message-timestamp">
              {formatTimestamp(message.createdAt)}
            </span>
          </div>
        )}

        {/* ── No content yet: single inline badge ── */}
        {!isUser && !content && activeStatus && (
          <span className={`message-status ${activeStatus}-status`}>
            <i className="fa-solid fa-circle-notch fa-spin" />
            {activeStatus === 'streaming' ? ' Executing...' : ' Processing...'}
          </span>
        )}

        {/* ── Content ── */}
        {content && (
          <>
            <div
              className={`message-content ${isUser ? 'user-message' : 'assistant-message'} ${isCollapsed ? 'collapsed' : ''}`}
            >
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>

            {canCollapse && (
              <button
                type="button"
                className="message-expand-btn"
                data-component-id="chat-message-show-more"
                onClick={() => setIsExpanded((v) => !v)}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}

        {/* ── Footer badge (only when content exists + still in-progress)  ──
            Ternary ensures only ONE badge ever renders per message.         ── */}
        {!isUser && content && activeStatus && (
          <div className="message-status-footer">
            {activeStatus === 'streaming' ? (
              <span className="message-status streaming-status">
                <i className="fa-solid fa-circle-notch fa-spin" /> Executing...
              </span>
            ) : (
              <span className="message-status processing-status">
                <i className="fa-solid fa-circle-notch fa-spin" /> Processing...
              </span>
            )}
          </div>
        )}

        {/* ── Message actions: only after completion ── */}
        {!isUser && isCompleted && (
          <MessageActions
            message={message}
            messageFeedback={messageFeedback}
            onFeedback={onFeedback}
            onCopy={onCopy}
          />
        )}

        {/* ── User timestamp ── */}
        {isUser && message.createdAt && (
          <div className="user-timestamp">{formatTimestamp(message.createdAt)}</div>
        )}

      </div>
    </div>
  );
};

const ChatMessage = ({ message, messageFeedback, onFeedback, onCopy, onPlanContinue, onPlanProgress }) => {
  if (isPlanChatMessage(message)) {
    return (
      <PlanQuestionCard
        message={message}
        onContinue={onPlanContinue}
        onProgress={onPlanProgress}
      />
    );
  }
  return (
    <RegularChatMessage
      message={message}
      messageFeedback={messageFeedback}
      onFeedback={onFeedback}
      onCopy={onCopy}
    />
  );
};

export default ChatMessage;
